import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import OmniIcon from '../components/icons/OmniIcon';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const LandingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isTauriDesktop = !!window.__TAURI_INTERNALS__;

    // Check for invite code in URL
    useEffect(() => {
        const inviteCode = searchParams.get('inviteCode');
        if (inviteCode) {
            localStorage.setItem('pendingInviteCode', inviteCode);
            console.log("Stored pending invite code:", inviteCode);
        }
    }, [searchParams]);

    // Handle auto-acceptance after login
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const inviteCode = localStorage.getItem('pendingInviteCode');
                if (inviteCode) {
                    try {
                        console.log("Attempting to accept invite...", inviteCode);
                        const acceptInvite = httpsCallable(functions, 'acceptInvite', { region: 'australia-southeast1' });
                        const result = await acceptInvite({ inviteCode });
                        localStorage.removeItem('pendingInviteCode');
                        if (result.data?.nodeId) {
                            navigate(`/space/${result.data.nodeId}`);
                        }
                    } catch (err) {
                        console.error("Auto-accept invite failure:", err);
                        // If it fails (e.g. already used), just clear it
                        localStorage.removeItem('pendingInviteCode');
                    }
                }
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    // Feature card component
    const FeatureCard = ({ title, description, icon }) => (
        <motion.div 
            whileHover={{ y: -4 }}
            className="p-6 rounded-2xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)] space-y-3"
        >
            <div className="w-10 h-10 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center text-[var(--accent-primary)] mb-4">
                {icon}
            </div>
            <h3 className="text-lg font-medium text-[var(--text-main)] tracking-tight">{title}</h3>
            <p className="text-sm text-[var(--text-muted)] leading-relaxed">{description}</p>
        </motion.div>
    );

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] selection:bg-[var(--accent-primary)]/10 selection:text-[var(--accent-primary)]">
            {/* Header / Nav */}
            <nav className="flex items-center justify-between px-4 md:px-8 py-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-2">
                    <OmniIcon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    <span className="text-lg md:text-xl font-bold tracking-tighter">Omni</span>
                </div>
                <div className="flex items-center gap-6">
                    <button 
                        onClick={() => navigate('/login')}
                        className="text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    >
                        Sign In
                    </button>
                    <button 
                        onClick={() => navigate('/login')}
                        className="px-4 py-2 rounded-full bg-[var(--accent-primary)] text-[var(--bg-main)] text-sm font-semibold hover:bg-[var(--accent-hover)] transition-all"
                    >
                        Get Started
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-20 md:pb-32">
                <div className="text-center space-y-6 md:space-y-8 max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="space-y-4"
                    >
                        <h1 className="text-4xl md:text-5xl lg:text-7xl font-light tracking-tighter leading-[1.1]">
                            Your universal <br />
                            <span className="text-[var(--text-muted)]">AI Operations hub.</span>
                        </h1>
                        <p className="text-base md:text-lg lg:text-xl text-[var(--text-muted)] max-w-2xl mx-auto font-medium tracking-tight px-2">
                            A premium workspace combining collaborative chat, agentic desktop control, and automated workflows into one seamless interface.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 px-4"
                    >
                        <button 
                            onClick={() => navigate('/login')}
                            className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-[var(--accent-primary)] text-[var(--bg-main)] font-semibold text-base hover:bg-[var(--accent-hover)] transition-all"
                        >
                            Start Building Now
                        </button>

                        {/* Download for Mac button - only visible in browser */}
                        {!isTauriDesktop && (
                            <a 
                                href="/downloads/Omni_latest_aarch64.dmg" 
                                download="Omni_latest_aarch64.dmg"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => console.log("Download triggered: Omni_latest_aarch64.dmg")}
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-2.5 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-accent)] text-[var(--text-main)] font-semibold text-base hover:bg-[var(--bg-secondary)]/80 transition-all"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download for Mac
                            </a>
                        )}
                    </motion.div>
                </div>

                {/* Grid of features */}
                <motion.div 
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-32"
                >
                    <FeatureCard 
                        title="Collaborative Chat"
                        description="Engage with advanced AI models and your team in high-context, threaded conversations."
                        icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
                    />
                    <FeatureCard 
                        title="Desktop Control"
                        description="Seamlessly control your local machine from anywhere. Vibe code on the go with full terminal access."
                        icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 21h6l-.75-4M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                    />
                    <FeatureCard 
                        title="Task Automation"
                        description="Define repeatable AI workflows that interact with your local systems and web APIs automatically."
                        icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                    />
                </motion.div>
            </main>

            {/* Visual background elements */}
            <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden">
                <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-cyan-500/5 blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-purple-500/5 blur-[120px]" />
            </div>

            {/* Footer */}
            <footer className="px-8 py-12 border-t border-[var(--border-subtle)] bg-[var(--bg-main)]">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2 opacity-50">
                        <OmniIcon className="w-5 h-5 text-white" />
                        <span className="text-sm font-bold tracking-tighter">Omni</span>
                    </div>
                    <p className="text-xs text-[var(--text-dim)] font-medium">
                        © 2026 Omni. Universal AI Operations.
                    </p>
                    <div className="flex gap-8">
                        <span className="text-xs text-[var(--text-dim)] hover:text-[var(--text-muted)] cursor-pointer">Terms</span>
                        <span className="text-xs text-[var(--text-dim)] hover:text-[var(--text-muted)] cursor-pointer">Privacy</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
